import { makeStyles, tokens } from "@fluentui/react-components";

const MAX_CONTENT_WIDTH = "720px";

export const usePageStyles = makeStyles({
  root: {
    height: "calc(100vh - 60px)", // Subtract header height
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
  },

  /* ---------- EMPTY ---------- */
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "16px",
    padding: "16px",
    minHeight: 0,
  },

  helperText: {
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
  },

  /* ---------- CHAT ---------- */
  messagesContainer: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },

  messagesScroller: {
    flex: 1,
    overflowY: "auto",
    padding: "20px 16px",
    minHeight: 0,

    scrollbarWidth: "thin",
    scrollbarColor: `${tokens.colorNeutralStroke3} transparent`,

    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: tokens.colorNeutralStroke3,
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: tokens.colorNeutralStroke2,
    },
  },

  messageRow: {
    display: "flex",
    marginBottom: "20px",
  },

  assistantRow: {
    justifyContent: "flex-start",
  },

  userRow: {
    justifyContent: "flex-end",
  },

  assistantMessage: {
    maxWidth: MAX_CONTENT_WIDTH,
    width: "100%",
  },

  userMessage: {
    maxWidth: "70%",
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    padding: "12px 16px",
    borderRadius: "12px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "14px",
    lineHeight: "1.5",
  },

  /* ---------- ATTACHMENTS ---------- */
  attachmentBlock: {
    marginTop: "12px",
    padding: "8px 12px",
    borderRadius: "8px",
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
    fontSize: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    maxHeight: "120px",
    overflowY: "auto",

    scrollbarWidth: "thin",
    scrollbarColor: `${tokens.colorNeutralStroke3} transparent`,

    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: tokens.colorNeutralStroke3,
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: tokens.colorNeutralStroke2,
    },
  },

  userAttachmentBlock: {
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    color: tokens.colorNeutralForegroundOnBrand,
    border: `1px solid rgba(255, 255, 255, 0.08)`,
  },

  /* ---------- COMPOSER ---------- */
  composerWrapper: {
    padding: "16px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
  },

  composer: {
    maxWidth: MAX_CONTENT_WIDTH,
    margin: "0 auto",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "16px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    backgroundColor: tokens.colorNeutralBackground3, // Different background for composer
    boxShadow: tokens.shadow8, // Stronger shadow for elevation
  },

  /* ---------- INPUT AREA ---------- */
  inputArea: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  textareaWrapper: {
    position: "relative",
    padding: "0px 6px",
  },

  textarea: {
    width: "100%",
    resize: "none",
    backgroundColor: "transparent",
    fontFamily: "inherit",
    fontSize: "15px",
    lineHeight: "1.6",
    maxHeight: "120px",
    minHeight: "24px",
    border: "none !important",
    outline: "none !important",
    boxShadow: "none !important",
    padding: "0",

    scrollbarWidth: "thin",
    scrollbarColor: `${tokens.colorNeutralForeground4} transparent`,

    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: tokens.colorNeutralForeground4,
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: tokens.colorNeutralForeground3,
    },

    "&::placeholder": {
      color: tokens.colorNeutralForeground4,
    },
    "&:hover, &:focus, &:focus-within, &:active": {
      border: "none !important",
      outline: "none !important",
      boxShadow: "none !important",
    },
    "&:focus-within::after": {
      border: "none !important",
      outline: "none !important",
      boxShadow: "none !important",
    },
    "&::after": {
      borderBottom: "0 px !important",
    },
    "& textarea": {
      backgroundColor: "transparent",
      color: tokens.colorNeutralForeground1,
      padding: "0",
      "&::placeholder": {
        color: tokens.colorNeutralForeground4,
      },
    },
  },

  /* ---------- BUTTONS ROW ---------- */
  buttonsRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  leftButtons: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },

  rightButtons: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },

  /* ---------- BUTTONS ---------- */
  iconButton: {
    width: "36px !important",
    height: "36px !important",
    minWidth: "36px !important",
    minHeight: "36px !important",
    padding: "0",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
  },

  attachButton: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      transform: "scale(1.05)",
    },
    "&:active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
      transform: "scale(0.95)",
    },
  },

  sendButton: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    "&:hover": {
      backgroundColor: tokens.colorBrandBackgroundHover,
      transform: "scale(1.05)",
    },
    "&:active": {
      backgroundColor: tokens.colorBrandBackgroundPressed,
      transform: "scale(0.95)",
    },
    "&:disabled": {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      color: tokens.colorNeutralForegroundDisabled,
      transform: "none",
      "&:hover": {
        backgroundColor: tokens.colorNeutralBackgroundDisabled,
        transform: "none",
      },
    },
  },

  stopButton: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
    "&:hover": {
      backgroundColor: tokens.colorPaletteRedBackground2,
      transform: "scale(1.05)",
    },
    "&:active": {
      backgroundColor: tokens.colorPaletteRedBackground2,
      transform: "scale(0.95)",
    },
  },

  /* ---------- ATTACHMENTS PREVIEW ---------- */
  attachmentPreview: {
    maxHeight: "400px", // Increased from 80px for better visibility
    overflowY: "auto",
    padding: "12px",
    borderRadius: "12px",
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
    fontSize: "13px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,

    scrollbarWidth: "thin",
    scrollbarColor: `${tokens.colorNeutralStroke3} transparent`,

    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: tokens.colorNeutralStroke3,
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: tokens.colorNeutralStroke2,
    },
  },

  /* ---------- TABLE ATTACHMENT ---------- */
  tableAttachment: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    borderRadius: "8px",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    padding: "8px",
  },

  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 4px 4px 8px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    minHeight: "32px",
    flexShrink: 0,
  },

  tableWrapper: {
    width: "100%",
    overflow: "hidden",
    position: "relative",
  },

  /* NEW: Horizontal scroll container */
  tableScrollContainer: {
    width: "100%",
    overflowX: "auto",
    overflowY: "auto",
    maxHeight: "150px", // Limit vertical height

    scrollbarWidth: "thin",
    scrollbarColor: `${tokens.colorNeutralStroke3} transparent`,

    "&::-webkit-scrollbar": {
      height: "8px", // Horizontal scrollbar height
      width: "6px", // Vertical scrollbar width
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: tokens.colorNeutralStroke3,
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: tokens.colorNeutralStroke2,
    },
  },

  dataTable: {
    width: "100%",
    minWidth: "max-content", // Forces table to expand beyond container if needed
    borderCollapse: "collapse",
    fontSize: "11px",

    "& thead": {
      position: "sticky",
      top: 0,
      zIndex: 10,
      backgroundColor: tokens.colorNeutralBackground1,
    },

    "& tbody tr:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },

    "& th, & td": {
      padding: "4px 8px",
      textAlign: "left",
      whiteSpace: "nowrap",
      maxWidth: "200px", // Prevent cells from growing too wide
      overflow: "hidden",
      textOverflow: "ellipsis",
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },

    "& th": {
      backgroundColor: tokens.colorNeutralBackground2,
      fontWeight: 600,
      position: "sticky",
      top: 0,
      zIndex: 10,
    },

    "& tr:last-child td": {
      borderBottom: "none",
    },
  },

  tableHeaderCell: {
    minWidth: "80px", // Ensures headers have minimum width
    maxWidth: "200px",
    position: "sticky",
    top: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    zIndex: 10,
  },

  tableCell: {
    minWidth: "60px",
    maxWidth: "200px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  /* Table attachment info text */
  tableInfo: {
    fontSize: "12px",
    fontWeight: 500,
    color: tokens.colorNeutralForeground2,
  },

  /* Remove button in table header */
  removeAttachmentButton: {
    minWidth: "auto",
    height: "24px",
    width: "24px",
    padding: "0",
    margin: "0 12px 0 0",
    borderRadius: "50%",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
  },

  /* ---------- ATTACHMENT ROW ---------- */
  attachmentRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "4px 0",
    minHeight: "24px",
  },

  /* ---------- ATTACHMENT ITEM ---------- */
  attachmentItem: {
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    borderRadius: "8px",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },

  /* ---------- ATTACHMENT COUNT ---------- */
  attachmentCount: {
    padding: "4px 8px",
    borderRadius: "6px",
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontSize: "12px",
    fontWeight: "600",
  },

  /* ---------- ATTACHMENT HEADER ---------- */
  attachmentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 0",
    flexShrink: 0,
  },

  /* ---------- TYPING INDICATOR ---------- */
  typingIndicator: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "12px 16px",
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: "12px",
    maxWidth: MAX_CONTENT_WIDTH,
  },

  typingDot: {
    color: tokens.colorNeutralForeground3,
    animationName: {
      "0%, 60%, 100%": {
        opacity: 0.3,
        transform: "translateY(0)",
      },
      "30%": {
        opacity: 1,
        transform: "translateY(-4px)",
      },
    },
    animationDuration: "1.4s",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
  },

  dot1: {
    animationDelay: "0s",
  },
  dot2: {
    animationDelay: "0.2s",
  },
  dot3: {
    animationDelay: "0.4s",
  },

  /* ---------- COMPOSER LABEL ---------- */
  composerLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: "12px",
    fontWeight: "500",
    paddingLeft: "4px",
  },
});
